import { useFrame } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { Camera, Object3D, Vector3 } from "three";
import { PERFORMANCE } from "./DevConstants";

// Distance thresholds for LOD levels (in world units)
const LOD_DISTANCES = {
  HIGH: PERFORMANCE.LOD.HIGH_DETAIL_DISTANCE,
  MEDIUM: PERFORMANCE.LOD.MEDIUM_DETAIL_DISTANCE,
  LOW: PERFORMANCE.LOD.LOW_DETAIL_DISTANCE,
};

// LOD level type
export type LODLevel = "HIGH" | "MEDIUM" | "LOW";

/**
 * Hook to determine the appropriate LOD level based on distance from camera
 * This hook must be used within a component that is a child of the Canvas component
 * @param object The 3D object to check distance for
 * @returns The current LOD level based on distance
 */
export const useLODLevel = (object: Object3D | null): LODLevel => {
  const [lodLevel, setLodLevel] = useState<LODLevel>("HIGH");
  const objectPosition = useRef(new Vector3());
  const cameraPosition = useRef(new Vector3());

  // This hook must be used within a component that is a child of the Canvas component
  useFrame(({ camera }) => {
    if (!object) return;
    
    // Get positions
    object.getWorldPosition(objectPosition.current);
    camera.getWorldPosition(cameraPosition.current);
    
    // Calculate distance
    const distance = objectPosition.current.distanceTo(cameraPosition.current);
    
    // Determine LOD level based on distance
    let newLodLevel: LODLevel;
    if (distance <= LOD_DISTANCES.HIGH) {
      newLodLevel = "HIGH";
    } else if (distance <= LOD_DISTANCES.MEDIUM) {
      newLodLevel = "MEDIUM";
    } else {
      newLodLevel = "LOW";
    }
    
    // Update LOD level if changed
    if (newLodLevel !== lodLevel) {
      setLodLevel(newLodLevel);
    }
  });

  return lodLevel;
};

// Shadow quality functions have been moved to AdaptiveShadows.tsx